import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  clientReportsApi,
  type ClientSupportReport,
  type ClientReportStatus,
  type ClientReportMessage,
} from '../../api/clientReports';
import { notifyError, notifySuccess } from '../../utils/notify';
import { useAdminAuthStore } from '../../store/adminAuthStore';

export const supportTicketTypeLabel = (t: string) => {
  if (t === 'PAYMENT_ISSUE') return 'Payment issue';
  if (t === 'ONBOARDING_ADDITIONAL_INFO') return 'Onboarding (additional info)';
  return 'General / other';
};

export const SUPPORT_STATUS_BADGE: Record<string, string> = {
  OPEN: 'bg-blue-100 text-blue-700',
  IN_PROGRESS: 'bg-yellow-100 text-yellow-700',
  RESOLVED: 'bg-green-100 text-green-700',
  CLOSED: 'bg-gray-100 text-gray-500',
};

export function wasSupportTicketReopened(r: ClientSupportReport) {
  return r.status === 'OPEN' && new Date(r.updatedAt).getTime() - new Date(r.createdAt).getTime() > 5000;
}

type Props = {
  ticketId: string | null;
  open: boolean;
  onClose: () => void;
  /** After reply or status save — e.g. reload onboarding details */
  afterChange?: () => void;
  getClientLabel?: (clientId: string) => string;
};

export function AdminSupportTicketModal({ ticketId, open, onClose, afterChange, getClientLabel }: Props) {
  const adminUser = useAdminAuthStore((state) => state.adminUser);
  const [detail, setDetail] = useState<ClientSupportReport | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editStatus, setEditStatus] = useState<ClientReportStatus>('OPEN');
  const [editNote, setEditNote] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [messages, setMessages] = useState<ClientReportMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const reloadMessages = useCallback(async (id: string) => {
    try {
      const res = await clientReportsApi.getMessages(id);
      setMessages(res.data);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch {
      setMessages([]);
    }
  }, []);

  useEffect(() => {
    if (!open || !ticketId) {
      setDetail(null);
      setLoadError(null);
      setMessages([]);
      setReplyText('');
      return;
    }

    let cancelled = false;
    setLoadError(null);
    setDetail(null);
    setMessagesLoading(true);
    void (async () => {
      try {
        const res = await clientReportsApi.getAdmin(ticketId);
        if (cancelled) return;
        const r = res.data;
        setDetail(r);
        setEditStatus((r.status?.toUpperCase() as ClientReportStatus) ?? 'OPEN');
        setEditNote(r.adminNote ?? '');
        await reloadMessages(ticketId);
      } catch (e: unknown) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Failed to load ticket');
      } finally {
        if (!cancelled) setMessagesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, ticketId, reloadMessages]);

  const handleClose = () => {
    setReplyText('');
    onClose();
  };

  const handleReply = async () => {
    if (!detail || !replyText.trim()) return;
    try {
      setReplying(true);
      await clientReportsApi.reply(detail.id, replyText.trim());
      notifySuccess('Reply sent.');
      setReplyText('');
      await reloadMessages(detail.id);
      afterChange?.();
      const fresh = await clientReportsApi.getAdmin(detail.id);
      setDetail(fresh.data);
    } catch (e: unknown) {
      notifyError(e, 'Failed to send reply');
    } finally {
      setReplying(false);
    }
  };

  const saveDetail = async () => {
    if (!detail) return;
    try {
      setSaveLoading(true);
      await clientReportsApi.updateAdmin(detail.id, { status: editStatus, adminNote: editNote || null });
      notifySuccess('Ticket updated.');
      const fresh = await clientReportsApi.getAdmin(detail.id);
      setDetail(fresh.data);
      afterChange?.();
      handleClose();
    } catch (e: unknown) {
      notifyError(e, 'Failed to update ticket');
    } finally {
      setSaveLoading(false);
    }
  };

  if (!open || !ticketId) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">Support ticket</h2>
            {loadError ? (
              <p className="mt-2 text-sm text-red-600">{loadError}</p>
            ) : detail ? (
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                    SUPPORT_STATUS_BADGE[detail.status] ?? 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {detail.status}
                </span>
                {wasSupportTicketReopened(detail) && (
                  <span className="text-xs font-medium text-orange-600">↩ Reopened by client</span>
                )}
              </div>
            ) : (
              <p className="mt-2 text-sm text-gray-500">Loading…</p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {detail ? (
              <Link
                to={`/support-tickets?ticket=${encodeURIComponent(detail.id)}`}
                className="text-xs font-medium text-indigo-600 underline hover:text-indigo-800"
                onClick={handleClose}
              >
                Open in Support
              </Link>
            ) : null}
            <button
              type="button"
              onClick={handleClose}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>

        {detail && !loadError ? (
          <>
            <div className="mb-3 flex flex-wrap gap-3 text-xs text-gray-500">
              <span>
                Type: <strong className="text-gray-700">{supportTicketTypeLabel(detail.reportType)}</strong>
              </span>
              <span>
                Client: <strong className="text-gray-700">{getClientLabel?.(detail.clientId) || `${String(detail.clientId).slice(0, 8)}…`}</strong>
              </span>
              {detail.outgoingPaymentAmount != null && (
                <span>
                  Payment:{' '}
                  <strong className="text-gray-700">
                    {detail.outgoingPaymentCurrency} {Number(detail.outgoingPaymentAmount).toFixed(2)}
                  </strong>
                </span>
              )}
            </div>
            <p className="mb-3 rounded-lg border border-gray-100 bg-gray-50 p-2 text-xs text-gray-700">
              <span className="font-medium text-gray-500">Description: </span>
              {detail.description}
            </p>

            <div className="mb-3 flex max-h-64 flex-col gap-2 overflow-y-auto rounded-xl border border-gray-100 bg-gray-50 p-3">
              {messagesLoading ? (
                <p className="py-4 text-center text-xs text-gray-400">Loading messages…</p>
              ) : messages.length === 0 ? (
                <p className="py-4 text-center text-xs text-gray-400">No messages yet.</p>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex max-w-[85%] flex-col ${
                      msg.senderType === 'ADMIN' ? 'self-end items-end' : 'self-start items-start'
                    }`}
                  >
                    <div
                      className={`whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm ${
                        msg.senderType === 'ADMIN'
                          ? 'rounded-br-sm bg-indigo-600 text-white'
                          : 'rounded-bl-sm border border-gray-200 bg-white text-gray-800'
                      }`}
                    >
                      {msg.body}
                    </div>
                    <span className="mt-0.5 px-1 text-[10px] text-gray-400">
                      {msg.senderType === 'ADMIN'
                        ? msg.senderDisplayName || (adminUser && msg.senderAdminId === adminUser.id
                          ? `${adminUser.firstName || ''} ${adminUser.lastName || ''}`.trim() || adminUser.email
                          : 'Support')
                        : 'Client'} ·{' '}
                      {new Date(msg.createdAt).toLocaleString('en-GB')}
                    </span>
                  </div>
                ))
              )}
              <div ref={chatEndRef} />
            </div>

            <div className="mb-4 flex gap-2">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void handleReply();
                  }
                }}
                rows={2}
                placeholder="Type a reply…"
                className="flex-1 resize-none rounded-lg border px-3 py-2 text-sm"
              />
              <button
                type="button"
                disabled={replying || !replyText.trim()}
                onClick={() => void handleReply()}
                className="h-[72px] self-end rounded-lg bg-indigo-600 px-4 text-sm font-medium text-white disabled:opacity-40"
              >
                {replying ? '…' : 'Send'}
              </button>
            </div>

            <label className="mb-1 block text-xs font-medium text-gray-700">Status</label>
            <select
              value={editStatus}
              onChange={(e) => setEditStatus(e.target.value as ClientReportStatus)}
              className="mb-3 w-full rounded-lg border px-3 py-2 text-sm"
            >
              <option value="OPEN">OPEN</option>
              <option value="IN_PROGRESS">IN_PROGRESS</option>
              <option value="RESOLVED">RESOLVED</option>
              <option value="CLOSED">CLOSED</option>
            </select>
            <label className="mb-1 block text-xs font-medium text-gray-700">Internal note (visible to client)</label>
            <textarea
              value={editNote}
              onChange={(e) => setEditNote(e.target.value)}
              rows={3}
              className="mb-4 w-full rounded-lg border px-3 py-2 text-sm"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg bg-gray-100 px-4 py-2 text-sm"
                onClick={handleClose}
              >
                Close
              </button>
              <button
                type="button"
                disabled={saveLoading}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white disabled:opacity-50"
                onClick={() => void saveDetail()}
              >
                {saveLoading ? 'Saving…' : 'Save status / note'}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
